import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { expandByVariant1 } from "@/utils/variantExpand";
import axios from "axios";
import BrandLogo from "@/components/BrandLogo";
import {
  ShoppingCart, Search, User, Menu, X, ChevronDown, ChevronRight,
  Star, SlidersHorizontal, Package, ArrowRight, RotateCcw,
  UserCircle, Heart, ChevronLeft
} from "lucide-react";
import VariantSwatches from "@/components/VariantSwatches";
import { quickAddToCart, getCartCount } from "@/utils/cart";
import { toast } from "sonner";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { ShopByPriceSection } from "@/pages/home/HomeSections";
import { useShopByPriceConfig } from "@/hooks/useShopByPriceConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 24;

function fmt(n) {
  if (!n && n !== 0) return "";
  return Number(n).toLocaleString("en-IN");
}
function discount(mrp, price) {
  if (!mrp || !price || mrp <= price) return 0;
  return Math.round(((mrp - price) / mrp) * 100);
}
function toSlug(name) {
  return name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
}
function fromSlug(slug) {
  return slug.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

// ── Stars ──────────────────────────────────────────────────────────────────
function Stars({ rating = 0, count }) {
  return (
    <div className="flex items-center gap-1">
      <div className="flex">
        {[1, 2, 3, 4, 5].map(i => (
          <Star
            key={i}
            className={`w-3 h-3 ${i <= Math.round(rating) ? "text-amber-400 fill-amber-400" : "text-slate-200 fill-slate-200"}`}
          />
        ))}
      </div>
      {count != null && <span className="text-[11px] text-slate-400">({count})</span>}
    </div>
  );
}

// ── Storefront Header (mini, shared style) ──────────────────────────────────
function StorefrontHeader({ cartCount, collections, slug }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const navigate = useNavigate();
  const catRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (catRef.current && !catRef.current.contains(e.target)) setCatOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQ.trim()) navigate(`/search?q=${encodeURIComponent(searchQ.trim())}`);
  };

  return (
    <>
      {/* Main Header */}
      <header className="bg-slate-900 text-white sticky top-0 z-40 shadow-lg">
        <div className="max-w-[1800px] mx-auto px-3 py-3 flex items-center gap-4">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="cat-header-logo">
            <BrandLogo size="md" textVariant="light" />
          </Link>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 max-w-2xl hidden sm:flex">
            <div className="flex w-full rounded-lg overflow-hidden border border-slate-700 bg-slate-800">
              <input
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                placeholder="Search products, brands and more..."
                className="flex-1 bg-transparent px-4 py-2 text-sm text-slate-200 placeholder-slate-500 outline-none"
              />
              <button type="submit" className="px-4 bg-orange-500 hover:bg-orange-600 transition-colors flex items-center">
                <Search className="w-4 h-4" />
              </button>
            </div>
          </form>

          {/* Actions */}
          <div className="flex items-center gap-3 ml-auto sm:ml-0">
            <Link to="/customer-login" className="hidden sm:flex items-center gap-1.5 text-slate-300 hover:text-white text-xs font-medium transition-colors">
              <UserCircle className="w-4 h-4" />
              Login
            </Link>
            <Link to="/cart" className="relative flex items-center gap-1.5 text-slate-300 hover:text-white text-xs font-medium transition-colors" data-testid="cat-cart-link">
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                  {cartCount > 9 ? "9+" : cartCount}
                </span>
              )}
            </Link>
            <button className="sm:hidden text-slate-400 hover:text-white" onClick={() => setMobileOpen(v => !v)}>
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile search */}
        <div className="sm:hidden px-4 pb-3">
          <form onSubmit={handleSearch} className="flex rounded-lg overflow-hidden border border-slate-700 bg-slate-800">
            <input
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              placeholder="Search..."
              className="flex-1 bg-transparent px-3 py-2 text-sm text-slate-200 placeholder-slate-500 outline-none"
            />
            <button type="submit" className="px-3 bg-orange-500 hover:bg-orange-600 transition-colors">
              <Search className="w-4 h-4" />
            </button>
          </form>
        </div>
      </header>

      {/* Category Nav */}
      <nav className="bg-slate-800 text-white">
        <div className="max-w-[1800px] mx-auto px-3">
          <div className="flex items-center gap-0">
            <div ref={catRef} className="relative flex-shrink-0">
              <button
                data-testid="cat-nav-all-categories"
                onClick={() => setCatOpen(v => !v)}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold bg-orange-500 hover:bg-orange-600 transition-colors"
              >
                <Menu className="w-4 h-4" />
                All Categories
                <ChevronDown className={`w-3 h-3 transition-transform ${catOpen ? "rotate-180" : ""}`} />
              </button>
              {catOpen && collections.length > 0 && (
                <div className="absolute top-full left-0 bg-white text-slate-800 shadow-2xl rounded-b-xl min-w-[220px] py-2 z-50 border border-slate-100">
                  {collections.map((col, i) => (
                    <Link
                      key={i}
                      to={`/c/${toSlug(col)}`}
                      onClick={() => setCatOpen(false)}
                      className={`flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-orange-50 hover:text-orange-600 transition-colors ${toSlug(col) === slug ? "bg-orange-50 text-orange-600 font-semibold" : ""}`}
                    >
                      <Package className="w-3.5 h-3.5 text-slate-400" />
                      {col}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <Link to="/" className="hidden md:flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-white hover:bg-slate-700 transition-colors flex-shrink-0">
              Home
            </Link>
          </div>
        </div>
      </nav>
    </>
  );
}

// ── Product Card ─────────────────────────────────────────────────────────────
function ProductCard({ product, onAddToCart }) {
  const disc = discount(product.mrp, product.deal_price_incl_gst);
  const price = product.deal_price_incl_gst || product.mrp || 0;
  // Use original product ID for wishlist (not the variant-expanded ID)
  const trackId = product._baseId || product.id;
  const [wishlisted, setWishlisted] = useState(() => {
    try { return JSON.parse(localStorage.getItem("it_wishlist") || "[]").includes(trackId); } catch { return false; }
  });

  const handleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const list = (() => { try { return JSON.parse(localStorage.getItem("it_wishlist") || "[]"); } catch { return []; } })();
    const next = list.includes(trackId) ? list.filter(id => id !== trackId) : [...list, trackId];
    localStorage.setItem("it_wishlist", JSON.stringify(next));
    window.dispatchEvent(new Event("wishlist-updated"));
    const added = !list.includes(trackId);
    setWishlisted(added);
    toast.success(added ? "Added to wishlist" : "Removed from wishlist");
  };

  // Build link: pre-select the variant on product page when expanded
  const productLink = product._variantExpanded
    ? `/products/${product._baseSlug}?${product._variantKey.toLowerCase()}=${encodeURIComponent(product._variantValue)}`
    : `/products/${product.url_slug || product.id}`;

  return (
    <div
      data-testid={`cat-product-card-${trackId}`}
      className="group bg-white rounded-xl border border-slate-100 overflow-hidden hover:shadow-md transition-all duration-200 flex flex-col"
    >
      <Link
        to={productLink}
        className="block relative overflow-hidden bg-slate-50"
        style={{ paddingBottom: "100%" }}
      >
        <img
          src={product.main_image || "https://images.unsplash.com/photo-1586953208270-d7db5e9ac21e?w=300"}
          alt={product.item_name}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
        {disc > 0 && (
          <span className="absolute top-2 left-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
            -{disc}%
          </span>
        )}
        {/* Variant label badge */}
        {product._variantExpanded && product._variantValue && (
          <span className="absolute bottom-2 left-2 bg-black/60 text-white text-[10px] font-medium px-2 py-0.5 rounded-full max-w-[80%] truncate">
            {product._variantValue}
          </span>
        )}
        {/* Wishlist heart */}
        <button
          onClick={handleWishlist}
          data-testid={`cat-wishlist-btn-${trackId}`}
          className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center shadow-sm transition-all opacity-0 group-hover:opacity-100 ${
            wishlisted ? "bg-red-50 opacity-100" : "bg-white"
          }`}
        >
          <Heart className={`w-3.5 h-3.5 ${wishlisted ? "fill-red-500 text-red-500" : "text-slate-400"}`} />
        </button>
        <button
          onClick={e => { e.preventDefault(); onAddToCart(product); }}
          className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-orange-500 hover:bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-md"
          data-testid={`cat-add-to-cart-${trackId}`}
        >
          <ShoppingCart className="w-3.5 h-3.5" />
        </button>
      </Link>

      <div className="p-3 flex flex-col flex-1">
        <Link to={productLink}>
          <p className="text-xs font-semibold text-slate-800 line-clamp-2 leading-snug mb-1 hover:text-orange-600 transition-colors">
            {product.item_name}
          </p>
        </Link>
        {product.brand_name && (
          <p className="text-[11px] text-slate-400 mb-1">{product.brand_name}</p>
        )}
        <Stars rating={product.star_rating} count={product.num_reviews} />
        {/* Show variant swatches only for non-expanded cards */}
        {!product._variantExpanded && <VariantSwatches product={product} max={4} />}
        <div className="mt-auto pt-2">
          <div className="flex items-baseline gap-1.5">
            <span className="text-sm font-black text-slate-900">₹{fmt(price)}</span>
            {product.mrp > price && (
              <span className="text-[11px] text-slate-400 line-through">₹{fmt(product.mrp)}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Filter Sidebar ───────────────────────────────────────────────────────────
// ── Horizontal Filter Bar ─────────────────────────────────────────────────────
function HorizontalFilterBar({ filters, setFilters, brands, onReset }) {
  const [priceOpen, setPriceOpen] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);
  const priceRef = useRef(null);
  const brandRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const h = (e) => {
      if (priceRef.current && !priceRef.current.contains(e.target)) setPriceOpen(false);
      if (brandRef.current && !brandRef.current.contains(e.target)) setBrandOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pricePresets = [
    { label: "Under ₹500", min: 0, max: 500 },
    { label: "₹500 – ₹2K", min: 500, max: 2000 },
    { label: "₹2K – ₹5K", min: 2000, max: 5000 },
    { label: "₹5K – ₹10K", min: 5000, max: 10000 },
    { label: "₹10K+", min: 10000, max: 0 },
  ];

  const hasPriceFilter = filters.minPrice > 0 || filters.maxPrice > 0;
  const hasBrandFilter = filters.brands.length > 0;
  const hasAnyFilter = hasPriceFilter || hasBrandFilter;

  const activePriceLabel = hasPriceFilter
    ? (pricePresets.find(p => p.min === filters.minPrice && p.max === filters.maxPrice)?.label
       || `₹${fmt(filters.minPrice)} – ${filters.maxPrice ? `₹${fmt(filters.maxPrice)}` : "Any"}`)
    : null;

  return (
    <div className="flex items-center gap-2 flex-wrap mb-4 bg-white rounded-xl border border-slate-100 px-4 py-2.5" data-testid="cat-filter-bar">
      <SlidersHorizontal className="w-4 h-4 text-slate-400 flex-shrink-0" />
      <span className="text-xs font-semibold text-slate-500 mr-1">Filters:</span>

      {/* Price Range */}
      <div className="relative" ref={priceRef}>
        <button
          onClick={() => { setPriceOpen(v => !v); setBrandOpen(false); }}
          data-testid="cat-filter-price-btn"
          className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border font-medium transition-all ${
            hasPriceFilter ? "border-orange-500 bg-orange-50 text-orange-600" : "border-slate-200 text-slate-600 hover:border-orange-300"
          }`}
        >
          {hasPriceFilter ? activePriceLabel : "Price Range"} <ChevronDown className="w-3 h-3" />
        </button>
        {priceOpen && (
          <div className="absolute top-full left-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-xl z-30 p-4 w-64">
            <p className="text-xs font-bold text-slate-700 mb-3">Price Range</p>
            <div className="space-y-1.5 mb-3">
              {pricePresets.map(({ label, min, max }) => (
                <button
                  key={label}
                  onClick={() => { setFilters(f => ({ ...f, minPrice: min, maxPrice: max, page: 1 })); setPriceOpen(false); }}
                  className={`w-full text-left text-xs px-3 py-2 rounded-lg font-medium transition-all ${
                    filters.minPrice === min && filters.maxPrice === max
                      ? "bg-orange-50 text-orange-600 border border-orange-200"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="border-t pt-3">
              <p className="text-[11px] text-slate-500 mb-2">Custom: Max ₹{fmt(filters.maxPrice) || "Any"}</p>
              <input
                type="range" min="0" max="50000" step="500"
                value={filters.maxPrice || 50000}
                onChange={e => setFilters(f => ({ ...f, maxPrice: parseInt(e.target.value), page: 1 }))}
                className="w-full accent-orange-500"
                data-testid="cat-filter-price-range"
              />
            </div>
          </div>
        )}
      </div>

      {/* Product Type / Brand */}
      {brands.length > 0 && (
        <div className="relative" ref={brandRef}>
          <button
            onClick={() => { setBrandOpen(v => !v); setPriceOpen(false); }}
            data-testid="cat-filter-brand-btn"
            className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border font-medium transition-all ${
              hasBrandFilter ? "border-orange-500 bg-orange-50 text-orange-600" : "border-slate-200 text-slate-600 hover:border-orange-300"
            }`}
          >
            {hasBrandFilter ? `Brand (${filters.brands.length})` : "Product Type"} <ChevronDown className="w-3 h-3" />
          </button>
          {brandOpen && (
            <div className="absolute top-full left-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-xl z-30 p-3 w-56 max-h-64 overflow-y-auto">
              <p className="text-xs font-bold text-slate-700 mb-2">Brand / Product Type</p>
              {brands.map(b => (
                <label key={b} className="flex items-center gap-2.5 cursor-pointer group py-1">
                  <input
                    type="checkbox"
                    checked={filters.brands.includes(b)}
                    onChange={e => setFilters(f => ({
                      ...f, page: 1,
                      brands: e.target.checked ? [...f.brands, b] : f.brands.filter(x => x !== b),
                    }))}
                    className="accent-orange-500 w-3.5 h-3.5"
                    data-testid={`cat-filter-brand-${b}`}
                  />
                  <span className="text-xs text-slate-700 group-hover:text-orange-600 transition-colors truncate">{b}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Active filter chips */}
      {hasBrandFilter && filters.brands.map(b => (
        <span key={b} className="inline-flex items-center gap-1 text-[11px] bg-orange-50 text-orange-600 border border-orange-200 rounded-full px-2 py-0.5 font-medium">
          {b}
          <button onClick={() => setFilters(f => ({ ...f, brands: f.brands.filter(x => x !== b), page: 1 }))}>
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}

      {/* Clear All */}
      {hasAnyFilter && (
        <button
          onClick={onReset}
          data-testid="cat-filter-reset"
          className="ml-auto text-xs text-orange-500 hover:text-orange-600 font-semibold flex items-center gap-1"
        >
          <RotateCcw className="w-3 h-3" /> Clear All
        </button>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function CategoryPage() {
  const { slug } = useParams();
  const [categoryConfig, setCategoryConfig] = useState(null);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [collections, setCollections] = useState([]);
  const [brands, setBrands] = useState([]);
  const [cartCount, setCartCount] = useState(getCartCount());
  const shopByPriceConfig = useShopByPriceConfig();

  const [filters, setFilters] = useState({
    minPrice: 0,
    maxPrice: 0,
    brands: [],
    sort: "newest",
    page: 1,
  });

  const sentinelRef = useRef(null);
  const pageRef = useRef(1);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Cart count sync
  const updateCart = useCallback(() => setCartCount(getCartCount()), []);
  useEffect(() => {
    window.addEventListener("it_cart_updated", updateCart);
    window.addEventListener("cart-updated", updateCart);
    return () => {
      window.removeEventListener("it_cart_updated", updateCart);
      window.removeEventListener("cart-updated", updateCart);
    };
  }, [updateCart]);

  // Fetch category config + collections on mount
  useEffect(() => {
    axios.get(`${API}/category/${slug}`).then(r => setCategoryConfig(r.data)).catch(() => {
      setCategoryConfig({ slug, name: fromSlug(slug), title: fromSlug(slug), banner_image: "", subtitle: "" });
    });
    axios.get(`${API}/collections/public`).then(r => setCollections(r.data)).catch(() => {});
  }, [slug]);

  // Build query params from filters
  const buildParams = useCallback((page, f) => {
    const p = new URLSearchParams();
    p.set("category", slug);
    p.set("paginated", "true");
    p.set("page", page);
    p.set("limit", PAGE_SIZE);
    if (f.sort) p.set("sort", f.sort);
    if (f.minPrice > 0) p.set("min_price", f.minPrice);
    if (f.maxPrice > 0) p.set("max_price", f.maxPrice);
    if (f.brands.length === 1) p.set("brand", f.brands[0]);
    return p.toString();
  }, [slug]);

  // Initial & filter-change load
  useEffect(() => {
    pageRef.current = 1;
    setLoadingInitial(true);
    setProducts([]);
    setHasMore(true);
    const qs = buildParams(1, filters);
    axios.get(`${API}/products/public?${qs}`)
      .then(r => {
        const data = r.data;
        setProducts(data.products || []);
        setTotal(data.total || 0);
        setHasMore(data.has_more || false);
        pageRef.current = 1;

        // Derive available brands from the fetched full list (use non-paginated for brands)
        return axios.get(`${API}/products/public?category=${slug}&limit=200`);
      })
      .then(r2 => {
        const all = Array.isArray(r2.data) ? r2.data : (r2.data.products || []);
        const uniqueBrands = [...new Set(all.map(p => p.brand_name).filter(Boolean))].sort();
        setBrands(uniqueBrands);
      })
      .catch(() => {})
      .finally(() => setLoadingInitial(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, filters.sort, filters.minPrice, filters.maxPrice, filters.brands.join(",")]);

  // Infinite scroll: load next page
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    const nextPage = pageRef.current + 1;
    setLoadingMore(true);
    const qs = buildParams(nextPage, filtersRef.current);
    axios.get(`${API}/products/public?${qs}`)
      .then(r => {
        const data = r.data;
        setProducts(prev => [...prev, ...(data.products || [])]);
        setHasMore(data.has_more || false);
        pageRef.current = nextPage;
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  }, [loadingMore, hasMore, buildParams]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting && !loadingMore) loadMore(); },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore, loadingMore]);

  const handleAddToCart = (product) => {
    quickAddToCart(product);
    toast.success("Added to cart!");
  };

  // Expand products by Variant 1 for grid display
  const expandedProducts = useMemo(() => expandByVariant1(products), [products]);

  const resetFilters = () => {
    setFilters({ minPrice: 0, maxPrice: 0, brands: [], sort: "newest", page: 1 });
  };

  const categoryTitle = categoryConfig?.title || categoryConfig?.name || fromSlug(slug);
  const categorySubtitle = categoryConfig?.subtitle || "";

  // Responsive banner: prefer mobile image on small screens
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const resizeHandler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", resizeHandler);
    return () => window.removeEventListener("resize", resizeHandler);
  }, []);
  const activeBannerImage = isMobile
    ? (categoryConfig?.banner_image_mobile || categoryConfig?.banner_image)
    : categoryConfig?.banner_image;

  const bannerBg = activeBannerImage
    ? { backgroundImage: `url(${activeBannerImage})`, backgroundSize: "cover", backgroundPosition: "center" }
    : { background: categoryConfig?.bg_color || "linear-gradient(135deg,#0f172a,#1e3a5f)" };

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteHeader
        cartCount={cartCount}
        collections={collections}
        showNav={true}
      />

      {/* Category Banner */}
      <div
        data-testid="cat-banner"
        className="relative overflow-hidden"
        style={{ minHeight: 180, ...bannerBg }}
      >
        {categoryConfig?.banner_image && (
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
        )}
        <div className="relative max-w-[1800px] mx-auto px-3 py-10 flex flex-col justify-end" style={{ minHeight: 180 }}>
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-xs text-white/60 mb-3">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/90 font-medium">{categoryTitle}</span>
          </nav>
          <h1 className="text-2xl sm:text-3xl font-black text-white leading-tight">{categoryTitle}</h1>
          {categorySubtitle && <p className="text-sm text-white/70 mt-1">{categorySubtitle}</p>}
          {total > 0 && (
            <p className="text-xs text-white/50 mt-2">{total.toLocaleString()} products</p>
          )}
        </div>
      </div>

      <div className="max-w-[1800px] mx-auto px-3 py-6">
        {/* Toolbar */}
        <div className="flex items-center justify-between mb-3 bg-white rounded-xl border border-slate-100 px-4 py-3">
          <span className="text-xs text-slate-400">
            {total > 0 ? `${total.toLocaleString()} products` : ""}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Sort by:</span>
            <select
              value={filters.sort}
              onChange={e => setFilters(f => ({ ...f, sort: e.target.value, page: 1 }))}
              className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 bg-white text-slate-700 focus:outline-none focus:border-orange-400"
              data-testid="cat-sort-select"
            >
              <option value="newest">Newest First</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
              <option value="rating">Top Rated</option>
            </select>
          </div>
        </div>

        {/* Horizontal Filter Bar */}
        <HorizontalFilterBar
          filters={filters}
          setFilters={setFilters}
          brands={brands}
          onReset={resetFilters}
        />

        {/* Product Grid */}
        {loadingInitial ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-100 overflow-hidden animate-pulse">
                <div className="bg-slate-200 w-full" style={{ paddingBottom: "100%" }} />
                <div className="p-3 space-y-2">
                  <div className="h-3 bg-slate-200 rounded w-full" />
                  <div className="h-3 bg-slate-200 rounded w-2/3" />
                  <div className="h-4 bg-slate-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-xl border border-slate-100">
            <Package className="w-12 h-12 text-slate-200 mb-4" />
            <p className="text-base font-bold text-slate-700 mb-1">No products found</p>
            <p className="text-sm text-slate-400 mb-4">Try removing some filters</p>
            <button
              onClick={resetFilters}
              className="text-sm font-semibold text-orange-500 hover:text-orange-600 flex items-center gap-1.5"
            >
              <RotateCcw className="w-4 h-4" /> Reset Filters
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="cat-product-grid">
              {expandedProducts.map(p => (
                <ProductCard key={p.id} product={p} onAddToCart={handleAddToCart} />
              ))}
            </div>

            {/* Infinite Scroll Sentinel */}
            <div ref={sentinelRef} className="py-6 flex items-center justify-center">
              {loadingMore && (
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                  Loading more...
                </div>
              )}
              {!hasMore && products.length > 0 && (
                <p className="text-xs text-slate-400 font-medium">All {total} products loaded</p>
              )}
            </div>
          </>
        )}

        {/* Shop by Price — replaces "You May Also Like" */}
        {!loadingInitial && shopByPriceConfig && (
          <div className="mt-8" data-testid="cat-shop-by-price">
            <ShopByPriceSection config={shopByPriceConfig} />
          </div>
        )}
      </div>

      <SiteFooter />
    </div>
  );
}
